import { useState, useEffect } from 'react';
import { useAuth } from '@/lib/auth';
import { prospectsAPI, prolongementsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { Plus, RefreshCcw, CheckCircle, Clock, Receipt, Zap, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const STATUS_CONFIG = {
    brouillon: { label: 'Brouillon', color: 'bg-amber-100 text-amber-700', icon: Clock },
    valide: { label: 'Validé', color: 'bg-green-100 text-green-700', icon: CheckCircle },
    facture: { label: 'Facturé', color: 'bg-blue-100 text-blue-700', icon: Receipt }
};

export function Prolongements({ prospectId, prospect, onRefresh }) {
    const { user } = useAuth();
    const [prolongements, setProlongements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [creating, setCreating] = useState(false);
    const [validating, setValidating] = useState(null);
    
    const [formData, setFormData] = useState({
        year: new Date().getFullYear() + 1,
        commission_ht: '',
        notes: ''
    });

    const isAdminFull = (user?.role === 'admin_full' || user?.is_admin === true);
    const isAdmin = (user?.role === 'admin' || user?.is_admin === true) || isAdminFull;
    const isVP = user?.role === 'vp';
    const isApporteur = user?.role === 'apporteur';
    const hasEasyPower = !!prospect?.easy_power_stage;

    useEffect(() => {
        if (prospectId && hasEasyPower) {
            loadProlongements();
        } else {
            setLoading(false);
        }
    }, [prospectId, hasEasyPower]);

    const loadProlongements = async () => {
        try {
            const response = await prospectsAPI.getProlongements(prospectId);
            setProlongements(response.data || []);
        } catch (error) {
            console.error('Error loading prolongements:', error);
            setProlongements([]);
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async () => {
        if (!formData.commission_ht || parseFloat(formData.commission_ht) <= 0) {
            toast.error('La commission HT est obligatoire');
            return;
        }

        setCreating(true);
        try {
            await prospectsAPI.createProlongement(prospectId, {
                prospect_id: prospectId,
                year: parseInt(formData.year),
                commission_ht: parseFloat(formData.commission_ht),
                notes: formData.notes || null
            });
            toast.success('Prolongement créé avec succès');
            setShowCreateDialog(false);
            setFormData({
                year: new Date().getFullYear() + 1,
                commission_ht: '',
                notes: ''
            });
            loadProlongements();
            if (onRefresh) onRefresh();
        } catch (error) {
            console.error('Error creating prolongement:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de la création');
        } finally {
            setCreating(false);
        }
    };

    const handleValidate = async (prolongementId) => {
        setValidating(prolongementId);
        try {
            await prolongementsAPI.validate(prolongementId);
            toast.success('Prolongement validé');
            loadProlongements();
            if (onRefresh) onRefresh();
        } catch (error) {
            console.error('Error validating prolongement:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
        } finally {
            setValidating(null);
        }
    };

    const formatCurrency = (value) => {
        if (value === null || value === undefined) return '-';
        return `${value.toLocaleString('fr-FR')} €`;
    };

    // Ne pas afficher si pas de pipeline Courtage
    if (!hasEasyPower) {
        return null;
    }

    return (
        <Card>
            <CardHeader className="flex flex-row items-center justify-between">
                <div>
                    <CardTitle className="flex items-center gap-2">
                        <RefreshCcw className="w-5 h-5 text-amber-600" />
                        Prolongements Courtage
                    </CardTitle>
                    <CardDescription>
                        Renouvellements annuels du contrat Courtage
                    </CardDescription>
                </div>
                {isAdmin && (
                    <Button 
                        size="sm" 
                        onClick={() => setShowCreateDialog(true)}
                        className="bg-amber-600 hover:bg-amber-700"
                        data-testid="create-prolongement-btn"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Nouveau prolongement
                    </Button>
                )}
            </CardHeader>
            <CardContent>
                {loading ? (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                    </div>
                ) : prolongements.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                        <RefreshCcw className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                        <p>Aucun prolongement enregistré</p>
                        {isAdmin && (
                            <p className="text-sm mt-2">
                                Créez un prolongement pour enregistrer un renouvellement annuel
                            </p>
                        )}
                    </div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Année</TableHead>
                                {isAdmin && <TableHead className="text-right">Commission HT</TableHead>}
                                {isAdminFull && <TableHead className="text-right">Restant Valnexo</TableHead>}
                                {isAdminFull && <TableHead className="text-right">Restant AT</TableHead>}
                                {(isAdmin || isVP) && <TableHead className="text-right">Commission VP</TableHead>}
                                <TableHead>Statut</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {prolongements.map((p) => {
                                const StatusIcon = STATUS_CONFIG[p.status]?.icon || Clock;
                                return (
                                    <TableRow key={p.id} data-testid={`prolongement-row-${p.id}`}>
                                        <TableCell className="font-medium">
                                            <div className="flex items-center gap-2">
                                                <Zap className="w-4 h-4 text-amber-500" />
                                                {p.year}
                                            </div>
                                        </TableCell>
                                        {isAdmin && (
                                            <TableCell className="text-right font-semibold">
                                                {formatCurrency(p.commission_ht)}
                                            </TableCell>
                                        )}
                                        {isAdminFull && (
                                            <TableCell className="text-right text-green-600">
                                                {formatCurrency(p.ep_restant_valnexo)}
                                            </TableCell>
                                        )}
                                        {isAdminFull && (
                                            <TableCell className="text-right text-blue-600">
                                                {formatCurrency(p.ep_restant_at_service)}
                                            </TableCell>
                                        )}
                                        {(isAdmin || isVP) && (
                                            <TableCell className="text-right text-purple-600">
                                                {formatCurrency(p.ep_commission_vp)}
                                            </TableCell>
                                        )}
                                        <TableCell>
                                            <Badge className={STATUS_CONFIG[p.status]?.color || 'bg-slate-100'}>
                                                <StatusIcon className="w-3 h-3 mr-1" />
                                                {STATUS_CONFIG[p.status]?.label || p.status}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {isAdminFull && p.status === 'brouillon' && (
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => handleValidate(p.id)}
                                                    disabled={validating === p.id}
                                                    data-testid={`validate-prolongement-${p.id}`}
                                                >
                                                    {validating === p.id ? (
                                                        <Loader2 className="w-4 h-4 animate-spin" />
                                                    ) : (
                                                        <>
                                                            <CheckCircle className="w-4 h-4 mr-1" />
                                                            Valider
                                                        </>
                                                    )}
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                )}
            </CardContent>

            {/* Dialog de création */}
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Nouveau Prolongement Courtage</DialogTitle>
                        <DialogDescription>
                            Enregistrez un renouvellement annuel pour ce contrat
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div>
                            <Label htmlFor="year">Année du prolongement</Label>
                            <Input
                                id="year"
                                type="number"
                                value={formData.year}
                                onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                                min={2020}
                                max={2100}
                                data-testid="prolongement-year-input"
                            />
                        </div>
                        <div>
                            <Label htmlFor="commission_ht">Commission HT (€) *</Label>
                            <Input
                                id="commission_ht"
                                type="number"
                                step="0.01"
                                value={formData.commission_ht}
                                onChange={(e) => setFormData({ ...formData, commission_ht: e.target.value })}
                                placeholder="Ex: 5000"
                                data-testid="prolongement-commission-input"
                            />
                            <p className="text-xs text-slate-500 mt-1">
                                La répartition des commissions sera calculée automatiquement
                            </p>
                        </div>
                        <div>
                            <Label htmlFor="notes">Notes (optionnel)</Label>
                            <Textarea
                                id="notes"
                                value={formData.notes}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                placeholder="Informations complémentaires..."
                                rows={3}
                                data-testid="prolongement-notes-input"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                            Annuler
                        </Button>
                        <Button 
                            onClick={handleCreate} 
                            disabled={creating}
                            className="bg-amber-600 hover:bg-amber-700"
                            data-testid="confirm-create-prolongement"
                        >
                            {creating ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : (
                                <Plus className="w-4 h-4 mr-2" />
                            )}
                            Créer le prolongement
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Card>
    );
}
